import React from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Building2,
  Users,
  UserCheck,
  Target,
  DollarSign,
  Percent,
  Calendar
} from 'lucide-react';

const ReportKPICards = ({ kpis = [], loading = false }) => {
  const getChangeIcon = (change) => {
    if (change > 0) return <TrendingUp size={16} className="text-green-600" />;
    if (change < 0) return <TrendingDown size={16} className="text-red-600" />;
    return <Minus size={16} className="text-gray-400" />;
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getIcon = (type) => {
    const iconMap = {
      sales: <div className="text-2xl font-bold text-blue-600">£</div>,
      customers: <Users size={24} className="text-green-600" />,
      advisors: <UserCheck size={24} className="text-purple-600" />,
      bdms: <Target size={24} className="text-orange-600" />,
      branches: <Building2 size={24} className="text-indigo-600" />,
      margin: <Percent size={24} className="text-emerald-600" />,
      growth: <TrendingUp size={24} className="text-rose-600" />,
      conversion: <Target size={24} className="text-cyan-600" />,
      retention: <Calendar size={24} className="text-amber-600" />,
      default: <div className="text-2xl font-bold text-gray-600">£</div>
    };
    return iconMap[type] || iconMap.default;
  };

  const formatValue = (value, format) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    
    // Ensure value is a number for numeric operations
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    
    // Check if parseFloat resulted in NaN
    if (isNaN(numValue)) return 'N/A';
    
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(numValue);
      case 'percentage':
        // Ensure valid percentage value
        if (isNaN(numValue) || !isFinite(numValue)) return 'N/A';
        return `${Math.max(0, Math.min(100, numValue)).toFixed(1)}%`;
      case 'number':
        return new Intl.NumberFormat('en-GB').format(numValue);
      case 'decimal':
        return numValue.toFixed(1);
      default:
        return value.toString();
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              <div className="w-16 h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="space-y-2">
              <div className="w-20 h-8 bg-gray-200 rounded"></div>
              <div className="w-24 h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!kpis || kpis.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <div className="text-gray-400 mb-2">
          <div className="text-4xl font-bold mx-auto">£</div>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No KPI Data Available</h3>
        <p className="text-gray-600">KPI metrics will appear here once data is loaded.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {kpis.map((kpi, index) => (
        <div 
          key={kpi.key || index}
          className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow ${
            kpi.type === 'sales' ? 'border-l-4 border-l-blue-500' :
            kpi.type === 'margin' ? 'border-l-4 border-l-emerald-500' :
            kpi.type === 'credits' ? 'border-l-4 border-l-red-500' :
            kpi.type === 'parts' ? 'border-l-4 border-l-green-500' :
            kpi.type === 'branches' ? 'border-l-4 border-l-indigo-500' :
            'border-l-4 border-l-gray-500'
          }`}
        >
          {/* Content */}
          <div className="space-y-2">
            <div className={`text-3xl font-bold ${kpi.color || 'text-gray-900'}`}>
              {formatValue(kpi.value, kpi.format)}
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-600">
                {kpi.label}
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                {getIcon(kpi.type)}
              </div>
            </div>
            {kpi.subtitle && (
              <div className="text-xs text-gray-500">
                {kpi.subtitle}
              </div>
            )}
            {/* Previous Period Comparison */}
            {kpi.previousValue !== undefined && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Previous: {formatValue(kpi.previousValue, kpi.format)}
                  </div>
                  {kpi.change !== undefined && (
                    <div className={`flex items-center space-x-1 ${getChangeColor(kpi.change)}`}>
                      {getChangeIcon(kpi.change)}
                      <span className="text-xs font-medium">
                        {kpi.change >= 0 ? '+' : ''}{kpi.change.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Fallback: Show change without previous value */}
            {kpi.previousValue === undefined && kpi.change !== undefined && (
              <div className={`flex items-center space-x-1 ${getChangeColor(kpi.change)}`}>
                {getChangeIcon(kpi.change)}
                <span className="text-sm font-medium">
                  {kpi.change >= 0 ? '+' : ''}{Math.abs(kpi.change).toFixed(1)}%
                </span>
              </div>
            )}
          </div>

          {/* Additional Info */}
          {kpi.additionalInfo && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs text-gray-500">
                {kpi.additionalInfo}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ReportKPICards;
